import { useQuery } from '@tanstack/react-query'
import { useStoProject } from './useStoProject'
import { stoQueries } from './queries/stoQueries'
import { pickContent, StoContentTypes, withAcceptedCurrencies } from 'models/projectContent'
import { resolveSectionState, type SectionState } from 'models/stoSection'
import { getMetricHref, resolveMetricValue, type LabelledFigure, type KeyInformationSection } from 'models/metric'
import type { MoneyDisplay } from 'shared/domain/projectTemplate'
import type { ComputedFields } from 'models/productChart'
import { useMoneyDisplay } from './useMoneyDisplay'
import { useComputedFields } from './useProductChart'

export interface KeyInformationRow {
  key: string
  label: string
  value: string
  href?: string
  tooltip?: string
  tooltipDisclaimer: boolean
  tooltipPastPerformanceDisclaimer: boolean
}

export interface KeyInformationGroup {
  key: string
  label: string
  rows: KeyInformationRow[]
}

const toRow = (
  entry: LabelledFigure,
  index: number,
  project: unknown,
  display?: MoneyDisplay,
  computed?: ComputedFields,
): KeyInformationRow | null => {
  const value = resolveMetricValue(entry.value, project, display, computed)
  if (value === undefined) return null

  return {
    key: `${index}-${entry.key}`,
    label: entry.label?.trim() ?? '',
    value,
    href: getMetricHref(value, entry.url),
    tooltip: entry.tooltip?.trim() || undefined,
    tooltipDisclaimer: Boolean(entry.tooltipDisclaimer && entry.tooltip?.trim()),
    tooltipPastPerformanceDisclaimer: Boolean(entry.tooltipPastPerformanceDisclaimer && entry.tooltip?.trim()),
  }
}

/** Same rules as the back-office preview: empty rows are left out, then empty sections. */
export const toKeyInformationGroups = (
  sections: KeyInformationSection[] | undefined,
  project: unknown,
  display?: MoneyDisplay,
  computed?: ComputedFields,
): KeyInformationGroup[] =>
  (sections ?? [])
    .map((section, index) => ({
      key: section.key || String(index),
      label: section.label?.trim() ?? '',
      rows: (section.entries ?? [])
        .map((entry, entryIndex) => toRow(entry, entryIndex, project, display, computed))
        .filter((row): row is KeyInformationRow => row !== null),
    }))
    .filter((group) => group.rows.length > 0)

export function useKeyInformation(): SectionState<KeyInformationGroup[]> {
  const { project, projectId } = useStoProject()
  const query = useQuery(stoQueries.content(projectId))
  const sections = pickContent<KeyInformationSection[]>(query.data, StoContentTypes.KEY_INFORMATION)
  const display = useMoneyDisplay()
  const computed = useComputedFields()

  return resolveSectionState({
    isPending: query.isPending,
    isError: query.isError,
    data: toKeyInformationGroups(sections, withAcceptedCurrencies(project, query.data), display, computed),
    isEmpty: (groups) => groups.length === 0,
  })
}
